// Adapted from pingdotgg/t3code (MIT), the "Follow-up behavior" row in
// apps/web/src/components/settings/SettingsPanels.tsx at 7202ba666 (#11964).
// Upstream offers Queue and Steer; Amoreg also offers Interrupt. The choice
// used to live in a hover box above the composer's send button.
import type { FollowUpBehavior } from "@amoreg/contracts";
import { DEFAULT_UNIFIED_SETTINGS } from "@amoreg/contracts/settings";

import {
  ACTIVE_TURN_COMPOSER_ACTIONS,
  alternateComposerDispatchAction,
  COMPOSER_DISPATCH_ACTION_COPY,
} from "../../composerDispatch";
import { Select, SelectItem, SelectPopup, SelectTrigger, SelectValue } from "../ui/select";
import { searchableSetting } from "./settingsSearch";
import { SettingResetButton, SettingsRow } from "./settingsLayout";

function isFollowUpBehavior(value: unknown): value is FollowUpBehavior {
  return ACTIVE_TURN_COMPOSER_ACTIONS.includes(value as FollowUpBehavior);
}

/** The Settings description, naming the alternate the Ctrl/⌘ click sends. */
export function followUpBehaviorDescription(value: FollowUpBehavior): string {
  const alternate = COMPOSER_DISPATCH_ACTION_COPY[alternateComposerDispatchAction(value)].label;
  return `What Send does while a reply is running. Hold Ctrl/⌘ when you click Send, or press Ctrl/⌘+Enter, to ${alternate.toLowerCase()} instead.`;
}

export function FollowUpBehaviorSettingRow({
  value,
  onChange,
}: {
  value: FollowUpBehavior;
  onChange: (value: FollowUpBehavior) => void;
}) {
  return (
    <SettingsRow
      {...searchableSetting("follow-up-behavior")}
      description={followUpBehaviorDescription(value)}
      resetAction={
        value !== DEFAULT_UNIFIED_SETTINGS.followUpBehavior ? (
          <SettingResetButton
            label="follow-up behavior"
            onClick={() => onChange(DEFAULT_UNIFIED_SETTINGS.followUpBehavior)}
          />
        ) : null
      }
      control={
        <Select
          value={value}
          onValueChange={(next) => {
            if (isFollowUpBehavior(next)) onChange(next);
          }}
        >
          <SelectTrigger className="w-full sm:w-40" aria-label="Follow-up behavior">
            <SelectValue>{COMPOSER_DISPATCH_ACTION_COPY[value].label}</SelectValue>
          </SelectTrigger>
          <SelectPopup align="end" alignItemWithTrigger={false}>
            {ACTIVE_TURN_COMPOSER_ACTIONS.map((action) => (
              <SelectItem hideIndicator key={action} value={action}>
                <span className="flex flex-col gap-0.5">
                  <span>{COMPOSER_DISPATCH_ACTION_COPY[action].label}</span>
                  <span className="text-muted-foreground text-xs">
                    {COMPOSER_DISPATCH_ACTION_COPY[action].description}
                  </span>
                </span>
              </SelectItem>
            ))}
          </SelectPopup>
        </Select>
      }
    />
  );
}
